export const KARAOKE_PLAY_MODES = ['live', 'video'];

/** Missing or empty means both, matching songs saved before the field existed. */
export const normalizeKaraokePlayModes = (value) => {
    const raw = Array.isArray(value) ? value : [];
    const modes = KARAOKE_PLAY_MODES.filter((mode) => raw.includes(mode));
    return modes.length ? modes : ['live', 'video'];
};

/**
 * Admin-facing track format.
 * - video_only: recorded videos only. The Karaoke Live schedule never applies.
 * - live: recorded videos at any time, and a live battle while a window is open.
 * - live_only: live battles only, and only while a window is open. Ranked.
 */
export const KARAOKE_FORMATS = {
    VIDEO_ONLY: 'video_only',
    LIVE: 'live',
    LIVE_ONLY: 'live_only',
};

export const karaokeFormatFromPlayModes = (playModes) => {
    const modes = normalizeKaraokePlayModes(playModes);
    const live = modes.includes('live');
    const video = modes.includes('video');
    if (live && !video) return KARAOKE_FORMATS.LIVE_ONLY;
    if (!live && video) return KARAOKE_FORMATS.VIDEO_ONLY;
    return KARAOKE_FORMATS.LIVE;
};

export const playModesFromKaraokeFormat = (format) => {
    if (format === KARAOKE_FORMATS.VIDEO_ONLY) return ['video'];
    if (format === KARAOKE_FORMATS.LIVE_ONLY) return ['live'];
    return ['live', 'video'];
};

export const karaokeFormatLabel = (format) => {
    if (format === KARAOKE_FORMATS.VIDEO_ONLY) return 'Video only';
    if (format === KARAOKE_FORMATS.LIVE_ONLY) return 'Live only';
    return 'Live and video';
};

export const KARAOKE_MODES = {
    SOLO: 'solo',
    GROUP: 'group',
};

export const EMPTY_ASSIGNED_LYRICS = {
    playerOne: [],
    playerTwo: [],
    playerThree: [],
    playerFour: [],
    bothPlayers: [],
};

export const resolveLyricSourcesForEdit = (karaoke) => {
    const legacy = karaoke?.lyrics || [];
    const soloLyrics = karaoke?.soloLyrics?.length ? karaoke.soloLyrics : legacy;
    const groupLyrics = karaoke?.groupLyrics?.length ? karaoke.groupLyrics : legacy;
    const lineSource = soloLyrics.length ? soloLyrics : groupLyrics;

    return { soloLyrics, groupLyrics, lineSource };
};

export const loadAssignedLyricsFromKaraoke = (lyrics = []) => {
    const assigned = {
        ...EMPTY_ASSIGNED_LYRICS,
        bothPlayers: [],
        playerOne: [],
        playerTwo: [],
        playerThree: [],
        playerFour: [],
    };

    lyrics.forEach((line, index) => {
        if (line.assignedTo === 0) assigned.bothPlayers.push(index);
        else if (line.assignedTo === 1) assigned.playerOne.push(index);
        else if (line.assignedTo === 2) assigned.playerTwo.push(index);
        else if (line.assignedTo === 3) assigned.playerThree.push(index);
        else if (line.assignedTo === 4) assigned.playerFour.push(index);
    });

    return assigned;
};

export const getLyricAssignment = (index, assignedLyrics) => {
    if (assignedLyrics.bothPlayers.includes(index)) return 0;
    if (assignedLyrics.playerOne.includes(index)) return 1;
    if (assignedLyrics.playerTwo.includes(index)) return 2;
    if (assignedLyrics.playerThree.includes(index)) return 3;
    if (assignedLyrics.playerFour.includes(index)) return 4;
    return null;
};

export const isFullyAssigned = (lines, assignedLyrics) => {
    const totalLyrics = lines.filter((line) => line.trim().length > 0).length;
    if (totalLyrics === 0) return false;

    const assignedCount = [
        ...(assignedLyrics.playerOne || []),
        ...(assignedLyrics.playerTwo || []),
        ...(assignedLyrics.playerThree || []),
        ...(assignedLyrics.playerFour || []),
        ...(assignedLyrics.bothPlayers || []),
    ].length;

    return assignedCount === totalLyrics;
};

// Drop one line index from an assignment map ({playerOne: [..indexes]} etc.)
// and shift every higher index down so assignments keep pointing at the same
// lyric lines after the line is deleted.
export const removeLineFromAssigned = (assignedLyrics, index) => {
    const updated = {};
    Object.keys(assignedLyrics).forEach((key) => {
        updated[key] = (assignedLyrics[key] || [])
            .filter((lineIndex) => lineIndex !== index)
            .map((lineIndex) => (lineIndex > index ? lineIndex - 1 : lineIndex));
    });
    return updated;
};

// Same re-indexing for the {index: {start, end}} timestamp map.
export const removeLineFromTimestamps = (lineTimestamps, index) => {
    const updated = {};
    Object.keys(lineTimestamps).forEach((key) => {
        const lineIndex = Number(key);
        if (lineIndex === index) return;
        updated[lineIndex > index ? lineIndex - 1 : lineIndex] =
            lineTimestamps[key];
    });
    return updated;
};

export const buildLyricsPayload = (lines, lineTimestamps, assignedLyrics) =>
    lines
        .map((text, index) => {
            if (!text || (typeof text === 'string' && text.trim() === '')) {
                return null;
            }

            return {
                text,
                startTime: lineTimestamps[index]?.start ?? 0,
                endTime: lineTimestamps[index]?.end ?? 0,
                assignedTo: getLyricAssignment(index, assignedLyrics) ?? 0,
            };
        })
        .filter(Boolean);
